// How big and how high unit glyphs are drawn (docs/ART-DIRECTION.md §2): the one sizing rule the GlyphLayer draws with
// and the VFX layer aims from, so an attack leaves the glyph that fired it at every zoom. Glyphs keep their screen
// size (role by silhouette at every zoom), so sizes are in CSS pixels scaled by the frame's world units per pixel.

import { TRANSPORT_MODE_KEYS, TRANSPORT_MODES, UNITS, UNIT_KEYS, keyOf, type Domain } from '@taxwar/content'
import { GLOBE_ALTITUDE } from '../geo/globeSpace'
import { TRANSPORT_GLYPHS, UNIT_GLYPHS } from './glyphShapes'

/** Screen height of a size-1 glyph in CSS pixels, so the theatre view reads as well as a close-up. */
const GLYPH_CSS_PX = 24
/** No glyph is drawn smaller than this, whatever its relative size: the smallest silhouettes must still read. */
const MIN_GLYPH_CSS_PX = 12
/** Close in, a size-1 glyph stops shrinking in the world at this size (render units), so glyphs grow on screen. */
const MIN_GLYPH_WORLD_SIZE = 0.006

/** Hover above the network altitude, in glyph sizes (ART-DIRECTION §2.2: air floats higher). */
export const DOMAIN_HOVER: Readonly<Record<Domain, number>> = { land: 0.3, sea: 0, air: 2.5 }

/** World size (render units) of a glyph of `relative` size, at `worldPerCssPixel` render units per CSS pixel. */
export function glyphWorldSize(relative: number, worldPerCssPixel: number): number {
  const screenSize = Math.max(MIN_GLYPH_CSS_PX, GLYPH_CSS_PX * relative) * worldPerCssPixel
  return Math.max(screenSize, MIN_GLYPH_WORLD_SIZE * relative)
}

/** World size of a unit type's glyph. */
export function unitGlyphSize(unitType: number, worldPerCssPixel: number): number {
  return glyphWorldSize(UNIT_GLYPHS[keyOf(UNIT_KEYS, unitType)].size, worldPerCssPixel)
}

/** Radius (render units) of the base of a unit type's glyph: the network altitude plus its domain's hover. */
export function unitGlyphAltitude(unitType: number, worldPerCssPixel: number): number {
  const key = keyOf(UNIT_KEYS, unitType)
  return GLOBE_ALTITUDE.network + DOMAIN_HOVER[UNITS[key].domain] * unitGlyphSize(unitType, worldPerCssPixel)
}

/** World size of a transport craft's glyph (its own silhouette per TransportModeCode, not a unit type). */
export function transportGlyphSize(mode: number, worldPerCssPixel: number): number {
  return glyphWorldSize(TRANSPORT_GLYPHS[keyOf(TRANSPORT_MODE_KEYS, mode)].size, worldPerCssPixel)
}

/** Radius (render units) of the base of a transport craft's glyph: the network altitude plus its domain's hover. */
export function transportGlyphAltitude(mode: number, worldPerCssPixel: number): number {
  return GLOBE_ALTITUDE.network + DOMAIN_HOVER[TRANSPORT_MODES[keyOf(TRANSPORT_MODE_KEYS, mode)].domain] * transportGlyphSize(mode, worldPerCssPixel)
}
